import {ApplicationInsightsEvents} from '../analytics';
import {AppEventProperty, AppPageViewProperty} from './types';

export interface IEventTracker {
  trackPageViewed: (name: (typeof PageNames)[keyof typeof PageNames], properties?: AppEventProperty) => Promise<void>;
  trackEvent: (eventName: string, properties?: AppEventProperty) => Promise<void>;
  trackError: (error: Error, properties?: AppEventProperty) => Promise<void>;
  startTrackEvent: (eventName: string) => Promise<void>;
  stopTrackEvent: (eventName: string, properties?: AppEventProperty) => Promise<void>;
}

let currentPageBeingLoaded: PageId | undefined;
let currentPageBeingLoadedStartTime: number | undefined;
let currentPageBeingLoadedProperties: AppEventProperty | undefined;
let firstPageVisited = false;
let firstPage: PageId | undefined;
let debugLogs: boolean = false;

let initialized: boolean;
let defaultProperties: AppEventProperty = {};
let innerTracker: IEventTracker | undefined;
const preLoadedPages: Set<PageId> = new Set();

const timeOutTracker: Partial<Record<PageId, string | number | NodeJS.Timeout>> = {};

function _verifySetup() {
  if (!initialized) throw Error('Calling tracking method before the tracker is initialized');
}

export function initializeEventTracker(
  trackerImplementation: IEventTracker,
  propertiesToAddToAllTelemetry: AppPageViewProperty,
  enableDebugLogs: boolean = false,
) {
  innerTracker = trackerImplementation;
  defaultProperties = propertiesToAddToAllTelemetry;
  initialized = true;
  debugLogs = enableDebugLogs;
}

export function trackNavigateToPage(pageId: PageId, isFirstPageOfSession: boolean, properties?: AppPageViewProperty) {
  _verifySetup();
  if (debugLogs) console.debug('trackNavigateToPage', pageId);
  if (isFirstPageOfSession) firstPage = pageId;
  defaultProperties.currentPageId = pageId;

  if (currentPageBeingLoaded !== pageId) {
    currentPageBeingLoaded = pageId;
    currentPageBeingLoadedStartTime = performance.now();
    currentPageBeingLoadedProperties = properties;
  }

  if (preLoadedPages.has(pageId)) {
    schedulePageView(pageId);
  }

  return Promise.resolve();
}

function schedulePageView(pageId: PageId) {
  let isFirstPageOfSession = !firstPageVisited && pageId === firstPage;
  if (isFirstPageOfSession) firstPageVisited = true;

  // It can happen that the page being navigate to has stale data, which means the page is loaded initially,
  // resulting in receiving isLoaded=true in this function, then the page realizes it's data is outdated and
  // starts reloading, resulting in receiving isLoaded=false. The timeout below gives a page X milliseconds
  // to fall back to a loading state after being loaded.
  const timeOut = 200;
  const timeOutId = setTimeout(() => {
    innerTracker!.trackPageViewed(PageNames[pageId], {
      isFirstPageOfSession: isFirstPageOfSession,
      duration: performance.now() - currentPageBeingLoadedStartTime! - timeOut,
      ...currentPageBeingLoadedProperties,
    });
    delete timeOutTracker[pageId];
    currentPageBeingLoaded = undefined;
    currentPageBeingLoadedStartTime = undefined;
    currentPageBeingLoadedProperties = undefined;
  }, timeOut);
  timeOutTracker[pageId] = timeOutId;
}

export function trackPageComponentedRendered(pageId: PageId, isLoaded: boolean) {
  _verifySetup();
  if (debugLogs) console.debug('trackPageComponentedRendered', pageId, isLoaded);
  if (!isLoaded) {
    // If we scheduled a pageViewEvent to be sent, cancel it, because the page already went to isLoading immediately
    if (pageId in timeOutTracker) {
      clearTimeout(timeOutTracker[pageId]);
      delete timeOutTracker[pageId];
    }

    // If the page was preloaded before, indicate it as not preloaded now
    if (preLoadedPages.has(pageId)) {
      preLoadedPages.delete(pageId);
    }
    return Promise.resolve();
  }

  if (!preLoadedPages.has(pageId)) {
    preLoadedPages.add(pageId);
  }

  if (!currentPageBeingLoaded) {
    // Nothing to be done here. The page is loaded before it's being navigated to.
    return Promise.resolve();
  }

  // Can happen when the load of a page finishes after the user already started navigating to a new page.
  if (currentPageBeingLoaded !== pageId) return Promise.resolve();

  schedulePageView(pageId);
  return Promise.resolve();
}

export function trackEvent<T extends keyof ApplicationInsightsEvents>(
  event: T,
  ...[properties]: ApplicationInsightsEvents[T] extends undefined
    ? [properties?: never]
    : [properties: ApplicationInsightsEvents[T]]
) {
  _verifySetup();
  const allProps = {...(properties || {}), ...defaultProperties};
  const name = 'OfficeShifts_' + event;
  innerTracker!.trackEvent(name, allProps);
  return Promise.resolve();
}

export function startTrackEvent(event: keyof ApplicationInsightsEvents) {
  _verifySetup();
  const name = 'OfficeShifts_' + event;
  innerTracker!.startTrackEvent(name);
  return Promise.resolve();
}

export function stopTrackEvent<T extends keyof ApplicationInsightsEvents>(
  event: T,
  ...[properties]: ApplicationInsightsEvents[T] extends undefined
    ? [properties?: never]
    : [properties: ApplicationInsightsEvents[T]]
) {
  _verifySetup();
  const allProps = {...(properties || {}), ...defaultProperties};
  const name = 'OfficeShifts_' + event;
  innerTracker!.stopTrackEvent(name, allProps);
  return Promise.resolve();
}

export function trackError(error: Error, properties?: AppEventProperty) {
  _verifySetup();
  const allProps = {...properties, ...defaultProperties};
  innerTracker!.trackError(error, allProps);
  return Promise.resolve();
}

export const PageNames = {
  home: 'Mapiq User - Home',
  calendar: 'Mapiq User - Calendar',
  hereAndNowExplore: 'Mapiq User - Here and Now Explore',
  hereAndNowRooms: 'Mapiq User - Here and Now Rooms',
  hereAndNowWorkspaces: 'Mapiq User - Here and Now Workspaces',
  profile: 'Mapiq User - Profile',
  connections: 'Mapiq User - Connections',
  notifications: 'Mapiq User - Notifications',
  appLinks: 'Mapiq User - App links',
} as const;

export type PageId = keyof typeof PageNames;
